import { useDebouncedCallback } from "use-debounce";
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import {
  CustomTableMenuItem,
  Loader,
  SelectionState,
  SelectionStatus,
  Table,
  TableInstance,
  useTheme,
} from "@monolith-forensics/monolith-ui";
import type { ButtonProps } from "@monolith-forensics/monolith-ui";
import type { WithTestId } from "@/types/testid";

import { GetEvidenceQuery } from "../evidence.api.types";
import { EvidenceQuery } from "../evidence.query";
import { Evidence } from "../evidence.types";
import { EvidenceAPI } from "../evidence.api";
import { getDateFormat } from "@/utils/date-format";
import {
  useEvidenceColumnDefinitions,
  useEvidenceFilterDefinitions,
} from "../Hooks";
import { EvidenceFlyout } from "../Flyouts";
import {
  BulkCocEvidenceModal,
  CreateEvidenceModal,
  EvidenceCocReportsModal,
  EvidenceDataImportModal,
  MigrateEvidenceModal,
  NestedViewInfoModal,
  hasSeenNestedViewInfo,
  markNestedViewInfoSeen,
} from "../Modals";
import { useCaseContext, useContactContext } from "@/Features";
import { usePermissions } from "@/hooks/usePermissions";
import { resolveFilterState, useTableState } from "@/Features/_shared";
import { useBulkCocActions } from "./useBulkCocActions";
import { useBulkPrintLabels } from "./useBulkPrintLabels";
import type { LabelLookups } from "@/Features/Labels/evidenceLabelLookups";
import { PrintLabelsModal } from "@/Features/Labels/Modals/PrintLabelsModal";
import {
  EvidenceOptionComponent,
  EvidenceTooltipContent,
} from "../components/EvidenceSelector";
import { InfoIcon, ListTreeIcon, UploadIcon } from "lucide-react";
import { useFeatures } from "@/contexts/FeaturesContext";

const DEFAULT_PAGE_SIZE = 100;
const NESTED_VIEW_KEY = ":is_nested";

// `revealRow` — expand a row's collapsed ancestors so the row itself renders —
// ships in the monolith-ui release that pairs with this change. Called
// structurally so the app still typechecks and runs against the currently
// installed build, where the method is simply absent. Inline the call once the
// monolith-ui dependency is bumped.
const revealTableRow = (table: TableInstance | undefined, rowData: unknown) => {
  const tree = table as unknown as
    | { revealRow?: (row: unknown) => void }
    | undefined;

  tree?.revealRow?.(rowData);
};

export type EvidenceTableHandle = {
  refresh: () => void;
} & Partial<TableInstance>;

export type EvidenceTableProps = {
  query: GetEvidenceQuery;
  tableRef?: React.MutableRefObject<EvidenceTableHandle | undefined>;
  stateStoreKey?: string;
  onEvidenceCreated?: () => void;
  onSelectionChange?: (selection: SelectionState) => void;
};

export const EvidenceTable: React.FC<EvidenceTableProps> = ({
  query,
  tableRef,
  stateStoreKey,
  onEvidenceCreated,
  onSelectionChange,
}) => {
  const theme = useTheme();
  const { data: caseData } = useCaseContext() || {};
  const inCaseContext = !!caseData; // Whether the user is in a case context

  const { data: contactData } = useContactContext() || {};
  const inContactContext = !!contactData; // Whether the user is in a contact context

  const { hasPermission, MONOLITH_PERMISSIONS } = usePermissions();
  const { hasFeatures, FEATURES } = useFeatures();
  const canImportCsvEvidence =
    hasFeatures([FEATURES.EVIDENCE_CSV_IMPORT]) &&
    hasPermission(MONOLITH_PERMISSIONS.EVIDENCE_CREATE);
  const allowAllBulkCoc =
    hasFeatures([FEATURES.EVIDENCE_ALLOW_ALL_BULK_COC]) || !!inCaseContext; // Allow all bulk coc if the user has the feature flag or is in a case context

  const canBulkUpdate = hasPermission(
    MONOLITH_PERMISSIONS.EVIDENCE_BULK_UPDATE
  );

  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const tableInstanceRef = useRef<TableInstance>();
  const [showCreateEvidenceModal, setShowCreateEvidenceModal] = useState(false);
  const [showMigrateModal, setShowMigrateModal] = useState(false);
  const [showDataImportModal, setShowDataImportModal] = useState(false);
  const [showNestedViewInfoModal, setShowNestedViewInfoModal] = useState(false);
  const [focusedRowData, setFocusedRowData] = useState<Evidence | null>(null);
  const [showFlyout, setShowFlyout] = useState(false);
  const [nested, setNested] = useState<boolean>(() => {
    try {
      return (
        localStorage.getItem(`${stateStoreKey}${NESTED_VIEW_KEY}`) === "true"
      );
    } catch (error) {
      console.error("error", error);
      return false;
    }
  });

  // Persisting the nested view is a convenience, not a requirement: storage can
  // be readable but reject writes (private browsing, quota, blocked site data).
  // An unguarded throw here would fail the table on mount, before the user ever
  // touches the toggle, so fall back to an unpersisted preference instead.
  useEffect(() => {
    try {
      localStorage.setItem(
        `${stateStoreKey}${NESTED_VIEW_KEY}`,
        nested ? "true" : "false"
      );
    } catch (error) {
      console.error("error", error);
    }
  }, [nested, stateStoreKey]);

  // The explainer fires on the transition into nested view, not on mount, so a
  // user who already has the view enabled from a previous session isn't
  // interrupted. The seen flag is written on open rather than on dismiss —
  // closing via the overlay or Escape still counts as having seen it.
  const handleToggleNested = useCallback(() => {
    const next = !nested;
    setNested(next);
    if (next && !hasSeenNestedViewInfo()) {
      markNestedViewInfoSeen();
      setShowNestedViewInfoModal(true);
    }
  }, [nested]);

  const tableState = useTableState<GetEvidenceQuery>(
    { ...query, include_children: nested ? 1 : 0 },
    stateStoreKey,
    DEFAULT_PAGE_SIZE
  );

  const evidenceUpdateMutation = EvidenceQuery.useMutateUpdateEvidence({
    query: tableState.resolvedQuery,
  });

  const bulkCoc = useBulkCocActions(
    tableState.selectionState,
    tableState.resolvedQuery
  );

  const {
    data,
    isLoading,
    isFetched,
    isFetchingNextPage,
    refetch,
    fetchNextPage,
  } = EvidenceQuery.useGetInfiniteEvidence(tableState.resolvedQuery);

  const records = useMemo(
    () =>
      data?.pages?.reduce<Evidence[]>((acc, page) => {
        return [...acc, ...page.data];
      }, []) || [],
    [data]
  );

  // Depth-first order — the sequence the rows appear in once every parent is
  // expanded. The flyout's next/previous buttons walk this instead of the page
  // data so nested children are part of the cycle rather than being skipped
  // over with the rest of their parent's subtree. Responses without
  // `include_children` carry no `children`, so this collapses to page order.
  const orderedRecords = useMemo(() => {
    const ordered: Evidence[] = [];

    const walk = (item: Evidence) => {
      ordered.push(item);
      item.children?.forEach(walk);
    };

    records.forEach(walk);

    return ordered;
  }, [records]);

  const bulkPrint = useBulkPrintLabels({
    selectionState: tableState.selectionState,
    resolvedQuery: tableState.resolvedQuery,
    orderedRecords,
  });

  const debouncedFetchNextPage = useDebouncedCallback(() => {
    fetchNextPage();
  }, 50);
  const debouncedSearch = useDebouncedCallback((value) => {
    tableState.setSearchState(value);
  }, 250);
  const handleRowUpdated = ({
    rowData,
    updatedData,
  }: {
    rowData: Evidence;
    updatedData: Partial<Evidence>;
  }) => {
    evidenceUpdateMutation.mutate({
      evidence_id: rowData.evidence_id,
      ...updatedData,
    });
  };
  const handleExportTable = useCallback(
    (option: string) => {
      let columns = (tableInstanceRef.current?.columnState ?? [])
        .filter((c) => c.dataField !== "Action")
        .map((c) => ({
          dataField: c.dataField,
          header: c.caption || c.dataField,
          caption: c.caption || c.dataField,
          visible: c.visible,
          dataType: c.dataType,
        }));

      if (option === "export:visible") {
        columns = columns.filter((c) => c.visible !== false);
      }

      EvidenceAPI.exportEvidenceList({
        query: tableState.resolvedQuery,
        columns,
        date_format: getDateFormat({ isMoment: true, includeTime: true }),
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      }).then((res) => {
        const { signedUrl, filename } = res;
        const el = document.createElement("a");
        el.href = signedUrl.replace(
          "http://localhost:3000",
          "http://localhost:3001"
        );
        el.download = filename;
        el.click();
        // remove snackbar
        el.remove();
      });
    },
    [tableState.resolvedQuery]
  );
  // Makes `rowData` the flyout's subject and keeps its row visible in the table
  // behind it: a child row isn't rendered at all while its parent is
  // collapsed, so stepping onto one would otherwise describe a row the user
  // can't see. `revealRow` expands the ancestors only, and no-ops for rows
  // that are already visible.
  const focusEvidenceRow = (rowData: Evidence) => {
    setFocusedRowData(rowData);
    revealTableRow(tableInstanceRef.current, rowData);
  };
  const handleActionButtonClick = (rowData: Evidence) => {
    focusEvidenceRow(rowData);
    setShowFlyout(true);
  };
  const handleActionSelected = useCallback(
    (action: string) => {
      const selectionTotal = tableState.selectionState?.totalSelected || 0;

      switch (action) {
        case "bulk-migrate":
          if (selectionTotal === 0) {
            enqueueSnackbar("Please select at least one evidence item.", {
              variant: "warning",
            });
            return;
          }
          setShowMigrateModal(true);
          break;
      }
    },
    [tableState.selectionState?.totalSelected, enqueueSnackbar]
  );
  const handleEvidenceEdited = () => {
    refetch();
  };
  const handleEvidenceDeleted = () => {
    setShowFlyout(false);
    setFocusedRowData(null);
    refetch();
  };
  const handleGetNextItem = (uuid: string) => {
    const current = orderedRecords.findIndex((i) => i.uuid === uuid);
    const nextItem = orderedRecords[current + 1] || orderedRecords[0];
    if (nextItem) focusEvidenceRow(nextItem);
  };
  const handleGetPrevItem = (uuid: string) => {
    const current = orderedRecords.findIndex((i) => i.uuid === uuid);
    const prevItem =
      orderedRecords[current - 1] || orderedRecords[orderedRecords.length - 1];
    if (prevItem) focusEvidenceRow(prevItem);
  };
  const handleCopyFinished = () => {
    refetch();
  };
  const handleFlyoutUpdate = ({
    rowData,
    updateData,
  }: {
    rowData: Evidence;
    updateData: Partial<Evidence>;
  }) => {
    EvidenceQuery.updateInfiniteEvidenceCache({
      queryClient,
      queryKey: EvidenceQuery.queryKeys.infiniteList(tableState.resolvedQuery),
      updateData: {
        evidence_id: rowData.evidence_id,
        ...updateData,
      },
    });
    refetch();
  };

  useImperativeHandle(
    tableRef,
    () => {
      return {
        ...tableInstanceRef.current,
        refresh: () => {
          refetch();
        },
      };
    },
    [refetch]
  );

  const filters = useEvidenceFilterDefinitions({ case_id: query.case_id });

  const {
    columnDefinitions,
    isLoading: isColumnDefinitionsLoading,
    isFetched: isColumnDefinitionsFetched,
  } = useEvidenceColumnDefinitions();

  const loading = isLoading || !isFetched || isFetchingNextPage;

  const showBulkActions =
    !!tableState.selectionState?.totalSelected &&
    tableState.selectionState.totalSelected > 0 &&
    bulkCoc.enabled;

  const customActionItems: CustomTableMenuItem[] = [
    ...(showBulkActions
      ? [
          {
            type: "menu" as const,
            position: "left" as const,
            options: {
              children: "Actions",
              data: [
                bulkCoc.cocMenuItems,
                bulkPrint.printMenuItem,
                {
                  label: "Migrate Evidence",
                  value: "bulk-migrate",
                  visible: !!inCaseContext && canBulkUpdate,
                  onClick: () => {
                    handleActionSelected("bulk-migrate");
                  },
                },
              ],
              arrow: true,
            },
          },
        ]
      : []),
  ];

  // The nested view toggle button added to the table menu
  customActionItems.push({
    type: "button",
    position: "right",
    options: {
      children: <ListTreeIcon size={14} />,
      style: {
        padding: "0px 4px",
        borderColor: nested ? theme.palette.primary.main : undefined,
        color: nested ? theme.palette.primary.main : undefined,
      },
      title: "Toggle Nested View",
      onClick: handleToggleNested,
    },
  });

  // Only offered while the view is on: it explains rules that don't apply
  // otherwise, and it would be noise on a flat table.
  if (nested) {
    customActionItems.push({
      type: "button",
      position: "right",
      options: {
        children: <InfoIcon size={14} />,
        title: "How Nested View Works",
        style: { padding: "0px 4px" },
        onClick: () => setShowNestedViewInfoModal(true),
      },
    });
  }

  if (canImportCsvEvidence) {
    customActionItems.push({
      type: "button",
      position: "right",
      options: {
        children: <UploadIcon size={14} />,
        title: "Import from CSV",
        style: { padding: "0px 4px" },
        onClick: () => setShowDataImportModal(true),
      },
    });
  }

  if (!isColumnDefinitionsFetched) {
    return <Loader />;
  }

  return (
    <>
      {showFlyout && focusedRowData && (
        <EvidenceFlyout
          show={showFlyout}
          onClose={() => {
            setShowFlyout(false);
            setFocusedRowData(null);
          }}
          defaultInfo={focusedRowData}
          query={{ evidence_id: focusedRowData.evidence_id }}
          onEdit={handleEvidenceEdited}
          onDelete={handleEvidenceDeleted}
          onNext={() => handleGetNextItem(focusedRowData.uuid)}
          onPrevious={() => handleGetPrevItem(focusedRowData.uuid)}
          onCopy={handleCopyFinished}
          onUpdate={handleFlyoutUpdate}
        />
      )}
      {showCreateEvidenceModal && (
        <CreateEvidenceModal
          show={showCreateEvidenceModal}
          includeCaseField={!inCaseContext}
          navigateToItem={!inCaseContext}
          defaultFormData={{
            case_id: inCaseContext ? caseData?.case_id : undefined,
            linked_contact: inContactContext
              ? {
                  label: contactData?.name,
                  value: contactData?.client_id,
                  data: contactData,
                }
              : null,
          }}
          onSubmit={() => {
            onEvidenceCreated?.();
            refetch();
            setShowCreateEvidenceModal(false);
          }}
          onClose={() => setShowCreateEvidenceModal(false)}
          onCancel={() => setShowCreateEvidenceModal(false)}
        />
      )}
      {inCaseContext && tableState.selectionState && showMigrateModal && (
        <MigrateEvidenceModal
          open={showMigrateModal}
          handleClose={() => setShowMigrateModal(false)}
          onSubmit={() => {
            setShowMigrateModal(false);
            tableRef?.current?.clearSelections?.();
            tableState.setSelectionState({
              selectedRowKeys: [],
              excludedRowKeys: [],
              selectionStatus: SelectionStatus.None,
              totalSelected: 0,
            });
            refetch();
          }}
          selectionState={{
            caseInfo: {
              case_id: caseData.case_id,
              uuid: caseData.uuid,
              case_number: caseData.case_number,
              case_ref: caseData.case_ref,
            },
            options: {
              selectionStatus: tableState.selectionState?.selectionStatus,
              selectedItems: tableState.selectionState?.selectedRowKeys,
              excludedItems: tableState.selectionState?.excludedRowKeys,
              totalSelected: tableState.selectionState?.totalSelected,
              query: tableState.resolvedQuery,
            },
            total: tableState.selectionState?.totalSelected || 0,
          }}
          caseInfo={caseData}
        />
      )}
      {bulkCoc.bulkCocType &&
        tableState.selectionState &&
        bulkCoc.showBulkCocModal && (
          <BulkCocEvidenceModal
            type={bulkCoc.bulkCocType}
            open={bulkCoc.showBulkCocModal}
            defaultFormData={bulkCoc.bulkCocFormData}
            handleClose={() => bulkCoc.setShowBulkCocModal(false)}
            onSubmit={() => refetch()}
            onCancel={() => bulkCoc.setShowBulkCocModal(false)}
            selectionState={{
              ...tableState.selectionState,
              query: tableState.resolvedQuery,
            }}
          />
        )}
      {bulkPrint.adapter && (
        <PrintLabelsModal<Evidence, LabelLookups>
          open
          onClose={bulkPrint.close}
          {...bulkPrint.adapter}
          renderItemOption={(option) => (
            <EvidenceOptionComponent option={option} />
          )}
          ItemTooltip={EvidenceTooltipContent}
        />
      )}
      {bulkCoc.showCocReportsModal && (
        <EvidenceCocReportsModal
          open={bulkCoc.showCocReportsModal}
          onClose={() => {
            bulkCoc.setShowCocReportsModal(false);
            bulkCoc.setCocReportsData(undefined);
          }}
          cocReportsData={bulkCoc.cocReportsData}
        />
      )}
      {showDataImportModal && (
        <EvidenceDataImportModal
          open={showDataImportModal}
          onClose={() => setShowDataImportModal(false)}
          onComplete={() => refetch()}
        />
      )}
      {showNestedViewInfoModal && (
        <NestedViewInfoModal
          open={showNestedViewInfoModal}
          onClose={() => setShowNestedViewInfoModal(false)}
        />
      )}
      <Table
        data={records}
        columnProps={columnDefinitions}
        keyField="uuid"
        virtualized={true}
        tableInstanceRef={tableInstanceRef}
        totalRecords={data?.pages?.[0]?.total || 0}
        loading={loading}
        focusedRowId={focusedRowData?.uuid}
        enableSelection={allowAllBulkCoc}
        enableActionButton={true}
        stateStorage={{
          enabled: !!stateStoreKey,
          key: stateStoreKey || "",
          type: "localStorage",
        }}
        onSelectionChange={(selection) => {
          tableState.setSelectionState(selection);
          onSelectionChange?.(selection);
        }}
        onActionButtonClick={handleActionButtonClick}
        onRowUpdated={handleRowUpdated}
        onScrollToBottom={debouncedFetchNextPage}
        onColumnHeaderClick={({ sort }) =>
          tableState.setSortState(sort || undefined)
        }
        enableSorting={true}
        enableColumnReorder={true}
        enableColumnResize={true}
        manualSearch={true}
        manualSorting={true}
        manualFiltering={true}
        manualExport={true}
        treeOptions={{
          enabled: nested,
          showGuides: true,
          selectionScope: "roots",
          showCollapsedChildCount: true,
        }}
        tableMenuOptions={{
          enabled: true,
          tableCountOptions: {
            recordsTotalEnabled: true,
            selectionTotalEnabled: true,
          },
          addButtonOptions: {
            enabled: true,
            label: "+ Add Evidence",
            buttonOptions: {
              "data-testid": "evidence-add-button",
            } as WithTestId<ButtonProps>,
            onClick: () => {
              setShowCreateEvidenceModal(true);
            },
          },
          columnSelectorOptions: {
            enabled: true,
          },
          searchOptions: {
            enabled: true,
            onSearch: debouncedSearch,
            placeholder: "Search Evidence...",
          },
          exportOptions: {
            enabled: true,
            onExport: handleExportTable,
          },
          filterOptions: {
            enabled: true,
            filterDefinitions: filters,
            onFilterChange: (query) => {
              // Always sync the query to the *effective* filter. When no rule
              // has a usable value yet this resolves to `undefined`, which
              // leaves the query key unchanged (no redundant fetch) but also
              // correctly clears a previously applied filter when its value is
              // removed — so the list refetches unfiltered instead of getting
              // stuck on a stale filter.
              tableState.setFilterState(resolveFilterState(query));
            },
          },
          customMenuItems: customActionItems,
        }}
      />
    </>
  );
};
